import { useMemo, useState } from "react"
import { InspectorPanelKeyValueDropdown } from "../InspectorPanelKeyValueDropdown"
import {
    hexColorSwatches,
    ColorSwatchCategory,
    minZoom,
    lineWidthNameByLineWidth,
    LineWidth,
} from "@nova/constants"
import { InspectorPanelSettingsFields } from "../InspectorPanelSettingsFields"
import * as Icons from "@nova/component-icons"
import { decodeNumberToColor, encodeColorToNumber } from "@nova/serializer/src/client/util"
import { ColorPalette } from "../icons/TODO-put-in-package/ColorPalette"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { updateText } from "features/pubsub/entityActions/cubes/text"
import { InspectorPanelKeyValueToggle } from "../InspectorPanelKeyValueToggle"
import { InspectorPanelKeyValueRenamable } from "../InspectorPanelKeyValueRenamable"
import { selectTextTemplate, updateTextTemplate } from "plugins/Toolbox/tool-registry"
import {
    OptimisticLocalStateParams,
    useDefinedOptimisticLocalState,
} from "../useOptimisticLocalState"
import {
    selectTextColorById,
    selectTextContentById,
    selectTextFontSizeById,
    selectTextIsBoldById,
    selectTextOutlineColorById,
    selectTextRotationById,
    selectTextOutlineWidthById,
    selectTextById,
} from "features/entity-caching/texts/selectors"
import { publishLocalText, setLocalText } from "plugins/Toolbox/Text/slice"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { InspectorPanelKeyValueLongText } from "../InspectorPanelKeyValueLongText"
import { thicknesses } from "../lines/constants"
import { LineThicknessPicker } from "../lines/LineThicknessPicker"
import { getViewport } from "features/map/GlobalViewState"
import { RootState } from "integrations/redux"
import { Selector } from "react-redux"

const MIN_FONT_SIZE = 1
const MAX_FONT_SIZE = 500000
const rotationRegex = /^(?:$|-$|[+-]?(?:\d+(?:\.\d*)?|\.\d+))$/
const DARK = 0xff
const WHITE = 0xffffffff

const textColorSwatches = hexColorSwatches[ColorSwatchCategory.Text].map((hex) =>
    encodeColorToNumber(hex)
)
const outlineColorSwatches = hexColorSwatches[ColorSwatchCategory.TextOutline].map((hex) =>
    encodeColorToNumber(hex)
)

function useInspectorPanelTextLayer({
    colorParams,
    isBoldParams,
    fontSizeParams,
    rotationParams,
    textContentParams,
    outlineParams,
    widthParams,
}: {
    colorParams: OptimisticLocalStateParams<number>
    isBoldParams: OptimisticLocalStateParams<boolean>
    fontSizeParams: OptimisticLocalStateParams<number>
    rotationParams: OptimisticLocalStateParams<number>
    textContentParams: OptimisticLocalStateParams<string>
    outlineParams: OptimisticLocalStateParams<number>
    widthParams: OptimisticLocalStateParams<LineWidth>
}) {
    const [openDropdownId, setOpenDropdownId] = useState<string>()
    const [color, setColor] = useDefinedOptimisticLocalState(colorParams)
    const [isBold, setIsBold] = useDefinedOptimisticLocalState(isBoldParams)
    const [fontSize, setFontSize] = useDefinedOptimisticLocalState(fontSizeParams)
    const [rotation, setRotation] = useDefinedOptimisticLocalState(rotationParams)
    const [textContent, setTextContent] = useDefinedOptimisticLocalState(textContentParams)
    const [outline, setOutline] = useDefinedOptimisticLocalState(outlineParams)
    const [width, setWidth] = useDefinedOptimisticLocalState(widthParams)
    return {
        openDropdownId,
        setOpenDropdownId,
        color,
        setColor,
        isBold,
        setIsBold,
        fontSize,
        setFontSize,
        rotation,
        setRotation,
        textContent,
        setTextContent,
        outline,
        setOutline,
        width,
        setWidth,
    }
}

export const InspectorPanelTextLayer = ({ id }: { id?: string }) => {
    const dispatch = useAppDispatch()
    const text = useAppSelector(selectTextById(id || ""))
    const textTemplate = useAppSelector(selectTextTemplate)

    const hookParams: Parameters<typeof useInspectorPanelTextLayer>[0] = useMemo(
        () =>
            !!id
                ? {
                      colorParams: {
                          selector: selectTextColorById(id) as Selector<RootState, number>,
                          updateFn: (color) => {
                              dispatch(updateText({ id, updates: { color } }))
                              dispatch(updateTextTemplate({ color }))
                              let contrastedOutlineColor: number | undefined
                              if (!textTemplate.outlineColor) {
                                  switch (color) {
                                      case WHITE:
                                          contrastedOutlineColor = DARK
                                          break

                                      default:
                                          contrastedOutlineColor = WHITE
                                          break
                                  }
                              }
                              if (contrastedOutlineColor) {
                                  dispatch(
                                      updateText({
                                          id,
                                          updates: { outlineColor: contrastedOutlineColor },
                                      })
                                  )
                              }
                          },
                          defaultValue: text.color || textColorSwatches[0],
                      },
                      isBoldParams: {
                          selector: selectTextIsBoldById(id) as Selector<RootState, boolean>,
                          updateFn: (isBold) => {
                              dispatch(updateText({ id, updates: { bold: isBold } }))
                              dispatch(updateTextTemplate({ isBold }))
                          },
                          defaultValue: text.bold || false,
                      },
                      fontSizeParams: {
                          selector: selectTextFontSizeById(id) as Selector<RootState, number>,
                          updateFn: (fontSize) => {
                              dispatch(updateText({ id, updates: { fontSize } }))
                              dispatch(
                                  updateTextTemplate({
                                      fontSize: {
                                          value: fontSize,
                                          zoomLevel: getViewport().zoom || minZoom,
                                      },
                                  })
                              )
                          },
                          defaultValue: text.fontSize || 100,
                      },
                      rotationParams: {
                          selector: selectTextRotationById(id) as Selector<RootState, number>,
                          updateFn: (rotation) => {
                              dispatch(updateText({ id, updates: { rotation } }))
                          },
                          defaultValue: text.rotation || 0,
                      },
                      textContentParams: {
                          selector: selectTextContentById(id) as Selector<RootState, string>,
                          updateFn: (/*textContent*/) => {
                              // let name = selectedLocalText?.name ?? entity.name
                              // const placeholderRegex = /^Text (\d+)$/
                              // if (name && (placeholderRegex.test(name) || startingTextContent === name)) {
                              //     name = textContent
                              // }
                              // dispatch(updateText({ id, updates: { text: textContent, name } }))
                          },
                          defaultValue: /*selectedLocalText?.text ??*/ text.text ?? "",
                      },
                      outlineParams: {
                          selector: selectTextOutlineColorById(id) as Selector<RootState, number>,
                          updateFn: (outlineColor) => {
                              dispatch(updateText({ id, updates: { outlineColor } }))
                              dispatch(updateTextTemplate({ outlineColor }))
                          },
                          defaultValue: text.outlineColor || outlineColorSwatches[0],
                      },
                      widthParams: {
                          selector: selectTextOutlineWidthById(id) as Selector<
                              RootState,
                              LineWidth
                          >,
                          updateFn: (outlineWidth) => {
                              dispatch(updateText({ id, updates: { outlineWidth } }))
                              dispatch(updateTextTemplate({ outlineWidth }))
                          },
                          defaultValue: text.outlineWidth || LineWidth.none,
                      },
                  }
                : {
                      colorParams: {
                          selector: () => textTemplate.color || textColorSwatches[0],
                          updateFn: (color) => {
                              dispatch(updateTextTemplate({ color }))
                          },
                          defaultValue: textTemplate.color || textColorSwatches[0],
                      },
                      isBoldParams: {
                          selector: () => textTemplate.isBold,
                          updateFn: (isBold) => {
                              dispatch(updateTextTemplate({ isBold }))
                          },
                          defaultValue: textTemplate.isBold,
                      },
                      fontSizeParams: {
                          selector: () => textTemplate.fontSize?.value || 100,
                          updateFn: (fontSize) => {
                              dispatch(
                                  updateTextTemplate({
                                      fontSize: {
                                          value: fontSize,
                                          zoomLevel: getViewport().zoom || minZoom,
                                      },
                                  })
                              )
                          },
                          defaultValue: textTemplate.fontSize?.value || 100,
                      },
                      rotationParams: {
                          selector: () => 0,
                          updateFn: () => undefined,
                          defaultValue: 0,
                      },
                      textContentParams: {
                          selector: () => "",
                          updateFn: () => undefined,
                          defaultValue: "",
                      },
                      outlineParams: {
                          selector: () => textTemplate.outlineColor || outlineColorSwatches[0],
                          updateFn: (outlineColor) => {
                              dispatch(updateTextTemplate({ outlineColor }))
                          },
                          defaultValue: textTemplate.outlineColor || outlineColorSwatches[0],
                      },
                      widthParams: {
                          selector: () => textTemplate.outlineWidth,
                          updateFn: (outlineWidth) => {
                              dispatch(updateTextTemplate({ outlineWidth }))
                          },
                          defaultValue: textTemplate.outlineWidth,
                      },
                  },
        [
            dispatch,
            id,
            text?.bold,
            text?.color,
            text?.fontSize,
            text?.outlineColor,
            text?.outlineWidth,
            text?.rotation,
            text?.text,
            textTemplate.color,
            textTemplate.fontSize?.value,
            textTemplate.isBold,
            textTemplate.outlineColor,
            textTemplate.outlineWidth,
        ]
    )

    const {
        openDropdownId,
        setOpenDropdownId,
        color,
        setColor,
        isBold,
        setIsBold,
        fontSize,
        setFontSize,
        rotation,
        setRotation,
        textContent,
        setTextContent,
        outline,
        setOutline,
        width,
        setWidth,
    } = useInspectorPanelTextLayer(hookParams)

    useEffectUnsafe(() => {
        return () => {
            dispatch(publishLocalText())
        }
    }, [])

    return (
        <InspectorPanelSettingsFields>
            {/* Color */}
            <InspectorPanelKeyValueDropdown
                id="color"
                isOpen={openDropdownId === "color"}
                setOpenDropdownId={setOpenDropdownId}
                keyTitle="Text Color"
                Icon={Icons.BucketColor}
                iconClasses="text-gray-0"
                iconAccentColor={decodeNumberToColor(color)}
            >
                <ColorPalette
                    swatches={textColorSwatches}
                    selectedColor={color}
                    setSelectedColor={setColor}
                    setOpenDropdownId={setOpenDropdownId}
                    allowColorPicker={false}
                />
            </InspectorPanelKeyValueDropdown>

            {/* Bold */}
            <InspectorPanelKeyValueToggle
                keyTitle="Bold"
                isToggled={isBold}
                setIsToggled={setIsBold}
                titleClassName="w-24"
            />

            {/* Font Size */}
            {!!id && (
                <InspectorPanelKeyValueRenamable
                    id="text-font-size"
                    keyTitle="Font Size"
                    inputValue={fontSize.toString()}
                    sanitizer={(value) => {
                        const number = Number(value.replace(/\D/g, ""))
                        const clamped = Math.min(Math.max(number, 0), MAX_FONT_SIZE)
                        return clamped.toString()
                    }}
                    onConfirm={(value) => setFontSize(Math.max(Number(value), MIN_FONT_SIZE))}
                />
            )}

            {/* Rotation */}
            {!!id && (
                <InspectorPanelKeyValueRenamable
                    id="text-rotation"
                    keyTitle="Rotation"
                    inputValue={rotation.toString()}
                    previewValue={rotation.toString() + " º"}
                    sanitizer={(value) => {
                        if (rotationRegex.test(value)) return value
                        return null
                    }}
                    onConfirm={(value) => {
                        if (!rotationRegex.test(value)) return
                        let angle = Number(value) % 360
                        if (angle < 0) angle += 360
                        setRotation(angle)
                    }}
                />
            )}
            {!!id && <div className="w-full h-px my-2 bg-gray-700" />}

            {/* Outline */}
            {!!id && (
                <InspectorPanelKeyValueDropdown
                    id="outline"
                    isOpen={openDropdownId === "outline"}
                    setOpenDropdownId={setOpenDropdownId}
                    keyTitle="Outline Color"
                    Icon={Icons.BucketColor}
                    iconClasses="text-gray-0"
                    iconAccentColor={decodeNumberToColor(outline)}
                >
                    <ColorPalette
                        allowColorPicker={false}
                        swatches={outlineColorSwatches}
                        selectedColor={outline}
                        setSelectedColor={setOutline}
                        setOpenDropdownId={setOpenDropdownId}
                    />
                </InspectorPanelKeyValueDropdown>
            )}

            {/* Thickness */}
            {!!id && (
                <InspectorPanelKeyValueDropdown
                    id="thickness"
                    isOpen={openDropdownId === "thickness"}
                    setOpenDropdownId={setOpenDropdownId}
                    keyTitle="Outline Width"
                    Icon={thicknesses[width]}
                    dropdownTitle={lineWidthNameByLineWidth[width]}
                    iconClasses="text-gray-0"
                >
                    <LineThicknessPicker
                        hasNoneOption
                        selectedThickness={width}
                        setSelectedThickness={setWidth}
                        setOpenDropdownId={setOpenDropdownId}
                    />
                </InspectorPanelKeyValueDropdown>
            )}

            {!!id && <div className="w-full h-px my-2 bg-gray-700" />}

            {!!id && (
                <InspectorPanelKeyValueLongText
                    id={text.id}
                    keyTitle="Content"
                    value={textContent}
                    onConfirm={(newText) => {
                        setTextContent(newText)
                        dispatch(setLocalText({ ...text, text: newText }))
                    }}
                />
            )}
        </InspectorPanelSettingsFields>
    )
}
